import { KeysToCamelCase, UserNavOverrides } from '@statsbomb/parachute-types';
import { atomWithMutation } from 'jotai-tanstack-query';
import { snakeCase } from 'lodash/fp';
import { rawUserNavOverridesAtom } from '@/atoms/queries/user';
import { fetchClientAtom } from '../../queries/client';

// Determines if the user is switching to a new experience by comparing navigation override data.
// returns `true` if any of the keys is switched to a new experience.
const isSwitchingToNewExperience = (currentNavOverrides: UserNavOverrides, newNavOverrides: UserNavOverrides) => {
  const navKeys = Object.keys(newNavOverrides) as (keyof UserNavOverrides)[];

  return navKeys.some(key => currentNavOverrides[key] && !newNavOverrides[key]);
};

export const patchUserNavOverridesAtom = atomWithMutation(get => {
  const { fetch } = get(fetchClientAtom);
  const mutationKey = ['patchUserNavOverrides'];
  const mutationFn = async (userNavOverrideMutation: KeysToCamelCase<UserNavOverrides>) => {
    const userNavOverrides = await get(rawUserNavOverridesAtom);

    const formattedUserNavOverrideMutation = Object.entries(userNavOverrideMutation).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [snakeCase(key)]: value,
      }),
      {},
    ) as UserNavOverrides;

    // Refetch data only when a user switches to the "new" experience, as edge cases exist where a user may already be on an HSB page.
    // No refetch is needed for switching to "classic" experience, as users are automatically navigated to the classic page.
    const isSwitchingToNewXp = isSwitchingToNewExperience(userNavOverrides.data, formattedUserNavOverrideMutation);

    try {
      await fetch(`/user/nav-overrides`, {
        method: 'PUT',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify(formattedUserNavOverrideMutation),
      });
    } catch {
      return { isSuccess: false };
    }

    if (isSwitchingToNewXp) {
      await userNavOverrides.refetch();
    }

    return { isSuccess: true };
  };

  return { mutationFn, mutationKey };
});
